import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import {NoteEditor} from 'notebook/notes/NoteEditorTypes';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {CrossTableCellColors} from 'notebook/tables/constants/CellConstants';
import {OutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {isStructuredTable} from 'notebook/TypePredicates';
import {NotePart} from 'notebook/util/NoteUtilConstants';

import {StructuredTableSpreadsheetProps, UnknownNoteLinkedGroup} from '../NoteLinkedGroupConstants';
import {isMetadataForRestoreForContainedNotePart} from './NoteLinkedGroupSelection';

// Reads each contained table's evaluated values out of the entry-level `computedTables` map.
export function makeTableItemPropsByName(
  group: UnknownNoteLinkedGroup,
  editor: NoteEditor,
  spreadsheetProps: StructuredTableSpreadsheetProps,
  cellColors: CrossTableCellColors,
  editingTable: string | null,
  computedTables: OutputTableMap,
  // Passed in explicitly (rather than read off `editor.props` inside this function) so the
  // caller's useMemo can list it as a dependency. `editor` is a stable reference, so reading
  // `showExcessLabels` from `editor.props` here would leave the memoized result stale when it
  // toggles on its own (e.g. as a formula edit starts).
  showExcessLabels: boolean
): {
  tableItemPropsByName: {[tableName: string]: TableItemProps};
  evaluatedTableMap: OutputTableMap;
} {
  const evaluatedTableMap: OutputTableMap = {};
  const tableItemPropsByName: {[tableName: string]: TableItemProps} = {};
  const {tableNamespace, selectorCounts} = editor.props;
  const selectors = editor.props.selectorsFromProps({note: editor.props.note});

  group.containedNoteParts.forEach((containedNotePart: NotePart) => {
    if (!containedNotePart.item) {
      return;
    }
    if (!isStructuredTable(containedNotePart.item)) {
      return;
    }
    const structuredTable = containedNotePart.item as UnknownStructuredTable;
    const table = getStructuredTableAdapters()[structuredTable.kind].toTable(structuredTable);
    const computedTable = computedTables[table.name];
    const validationSnapshot = editor.getValidationErrorSnapshot();

    const tableItemProps: TableItemProps = {
      tableNamespace,
      showExcessLabels,
      computedTable,
      validationErrors: validationSnapshot[table.name] ?? [],
      table,
      cellColors: (cellColors[table.name] ?? {}) as {[rowIndex: number]: {[columnIndex: number]: string}},
      isMaximized: false,
      isSplitScreen: false,
      isFocused: false,
      multiEditing: editingTable === table.name,
      selectors,
      selectorCounts,
      metadataForRestore: isMetadataForRestoreForContainedNotePart(
        spreadsheetProps.metadataForRestore,
        containedNotePart
      )
        ? spreadsheetProps.metadataForRestore
        : null,
    };
    tableItemPropsByName[table.name] = tableItemProps;
    if (computedTable != null) {
      evaluatedTableMap[table.name] = computedTable;
    }
  });
  return {
    tableItemPropsByName,
    evaluatedTableMap,
  };
}
